import { Loading } from "../../app/Status";
import { useState } from "react";
import { Link } from "react-router";
import { coversWork, type ContractDetailDto, type UncoveredObligationDto } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg, useContract, useContracts, useContractsRefresh } from "./useContracts";

/**
 * Gekozen uitvoeringen uit *Zonder contract* onder een post brengen. Alleen contracten die alle gebouwen van de
 * keuze dekken en nog lopen (of ingaan) staan in de lijst.
 */
export function BringUnderDialog({
  rows,
  onClose,
  onDone,
}: {
  rows: UncoveredObligationDto[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const list = useContracts();
  const buildingIds = [...new Set(rows.map((r) => r.building.id))];
  const options = (list.data ?? []).filter(
    (c) => coversWork(c.state) && buildingIds.every((id) => c.buildings.some((b) => b.id === id)),
  );
  const [contractId, setContractId] = useState("");
  const chosen = contractId || options[0]?.id || "";
  const detail = useContract(chosen);
  const lines = (detail.data?.lines ?? []).filter(
    (l) => !l.building || buildingIds.every((id) => id === l.building!.id),
  );
  const [lineId, setLineId] = useState("");
  const line = lines.find((l) => l.id === lineId) ?? lines[0];
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!line) return;
    setBusy(true);
    try {
      const d = await api<ContractDetailDto>(`/contract-lines/${line.id}/obligations`, {
        method: "POST",
        body: { add: rows.map((r) => r.id) },
      });
      await refresh(d);
      toast(`${rows.length === 1 ? "1 uitvoering" : `${rows.length} uitvoeringen`} onder ${d.name}`);
      onDone();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Onder contract brengen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !line}>
            Onder contract brengen
          </button>
        </>
      }
    >
      <p className="muted small">
        {rows.length === 1 ? "1 uitvoering" : `${rows.length} uitvoeringen`} in{" "}
        {buildingIds.length === 1 ? "1 gebouw" : `${buildingIds.length} gebouwen`}. De leverancier van het contract
        wordt de vaste uitvoerder.
      </p>
      {list.isPending ? (
        <Loading />
      ) : !options.length ? (
        <p className="muted">
          Geen lopend contract dat {buildingIds.length === 1 ? "dit gebouw" : "al deze gebouwen"} dekt.{" "}
          <Link to="/contracten">Leg eerst een contract vast</Link>.
        </p>
      ) : (
        <>
          <label className="field">
            <span>Contract</span>
            <select
              value={chosen}
              onChange={(e) => {
                setContractId(e.target.value);
                setLineId("");
              }}
            >
              {options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.supplier ? ` · ${c.supplier.name}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Post</span>
            {detail.isPending ? (
              <Loading small lines={1} />
            ) : lines.length ? (
              <select value={line?.id ?? ""} onChange={(e) => setLineId(e.target.value)}>
                {lines.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.description}
                  </option>
                ))}
              </select>
            ) : (
              <span className="muted small">
                Dit contract heeft nog geen post. <Link to={`/contracten/${chosen}?tab=posten`}>+ Post</Link>
              </span>
            )}
          </label>
        </>
      )}
    </Modal>
  );
}
